import type { Scale } from "chart.js";
import { color } from "chart.js/helpers";
import type { Ref } from "vue";

import { toMoneyWithSign } from "@/shared/utils/format";

import type { ChartLabelMapping } from "./chartData";

// up to this many periods every one is labelled, above it only every 2nd
const maxLabelledPeriods = 28;

export function shortMonthsScale(rotation: number = 90) {
    return {
        display: true,
        title: {
            display: false,
        },
        grid: {
            display: false,
        },
        ticks: {
            maxRotation: rotation,
            minRotation: rotation,
            autoSkip: false,
            // maxTicksLimit: 6, // limit to 6 months
            callback: function (this: Scale, value, index, ticks) {
                return ticks.length > maxLabelledPeriods && index % 2 === 1 ? null : this.getLabelForValue(value);
            },
        },
    };
}

export function categoriesScale(labels: string[]) {
    return {
        display: true,
        title: {
            display: false,
        },
        grid: {
            display: false,
        },
        ticks: {
            maxRotation: 90,
            minRotation: 45,
            // maxTicksLimit: 6,
            callback: (index) => {
                const label = labels[index];
                return label.length > 25 ? label.substring(0, 25) + "..." : label;
            },
        },
    };
}

export function categoriesScaleRef(labels: Ref<string[]>) {
    return {
        display: true,
        title: {
            display: false,
        },
        grid: {
            display: false,
        },
        ticks: {
            maxRotation: 90,
            minRotation: 45,
            autoSkip: false,
            // maxTicksLimit: 6,
            callback: (index) => {
                const label = labels.value[index];
                return label.length > 25 ? label.substring(0, 25) + "..." : label;
            },
        },
    };
}

export function moneyScale() {
    return {
        display: true,
        beginAtZero: true,
        title: {
            display: false,
        },
        grid: {
            display: true,
        },
        ticks: {
            maxTicksLimit: 4,
            callback: (value) => {
                return toMoneyWithSign(value as number, true, undefined, false);
            },
        },
        suggestedMax: 100,
    };
}

// layout
export const defaultLineChartLayout = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
        padding: {
            left: 20,
            right: 30,
            top: 0,
            bottom: 10,
        },
        autoPadding: false,
    },
};

export const defaultBarChartLayout = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
        padding: {
            left: 10,
            right: 20,
            top: 0,
            bottom: 10,
        },
        autoPadding: false,
    },
};

// plugins
export const visibleLegend = {
    display: true,
};

export const hiddenLegend = {
    display: false,
};

// tooltip
export function defaultTooltip() {
    return {
        ...defaultTooltipSettings,
        callbacks: {
            label: (context) => displayPointMoneyValue(context),
        },
    };
}
export const defaultTooltipSettings = {
    // mode: "nearest",
    enabled: true,
    intersect: false,
    padding: 10,
    caretPadding: 5,
};

export const displayAssignedTooltipLabel = (context, chartLabels: ChartLabelMapping[]) => {
    const xAxisIndex = context[0].parsed.x;
    return chartLabels[xAxisIndex].tooltipLabel;
};

export const displayPointMoneyValue = (context) => {
    const value = context.dataset.data[context.dataIndex];
    return toMoneyWithSign(value as number, true);
};

export const fullChartHeight = "277px";

// default bar style
export function barStyle(borderColor: string, backgroundColor: string) {
    return {
        backgroundColor: color(backgroundColor).alpha(0.8).rgbString(),
        borderColor: color(borderColor).alpha(0.8).rgbString(),
        borderWidth: 1,
        borderRadius: 0,
    };
}

export function barStyleDefaultColors() {
    return {
        borderWidth: 1,
        borderRadius: 0,
    };
}
